import { Route, Routes, useLocation } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { PageContainers } from '@components/page-containers';
import { useScopedTranslate } from '@locale/translate';

import { Header } from 'components/headers';
import { Page, PageContent } from 'components/Page';
import { usePermissions } from 'features/permissions';
import { ADManagerSettingsPage } from 'integrations/adManager/settings-page';
import { AsanaSettingsPage } from 'integrations/asana/SettingsPage';
import { DokobitSettingsPage } from 'integrations/dokobit/settings-page';
import { GetAcceptSettingsPage } from 'integrations/getaccept/SettingsPage';
import { H3CoreSettingsPage } from 'integrations/H3core/SettingsPage';
import { HireflixSettingsPage } from 'integrations/hireflix/SettingsPage';
import { HRMonitorSettingsPage } from 'integrations/hrMonitor/SettingsPage';
import {
  JourneysActions,
  JourneysHeaderTabs,
  JourneysSettingsPage,
} from 'integrations/journeys/settings-page';
import { KjarniSettingsPage } from 'integrations/kjarni/SettingsPage';
import { MyTimePlanSettingsPage } from 'integrations/mytimeplan/settings-page';
import { OnboardingFormSettingsPage } from 'integrations/onboardingForm/settings-page';
import { OneCRMSettingsPage } from 'integrations/onecrm/settings-page';
import { RelesysSettingsPage } from 'integrations/relesys/SettingsPage';
import { SignetSettingsPage } from 'integrations/signet/settings-page';
import { SlackSettingsPage } from 'integrations/slack/Slack';
import { TaktikalSettingsPage } from 'integrations/taktikal/settings-page';
import { TimonSettingsPage } from 'integrations/timon/settings-page';

import { IntegrationItems } from './integrationsInfo';

export const Integrations = () => {
  const { t } = useScopedTranslate(locale);
  const location = useLocation();
  const currentRoute = location.pathname.split('/').pop() ?? '';
  const routeMap: { [key: string]: string } = {
    slack: t('slack'),
    getaccept: t('getaccept'),
    onboardingForm: t('onboardingform'),
    kjarni: t('kjarni'),
    asana: t('asana'),
    hrmonitor: t('hrmonitor'),
    relesys: t('relesys'),
    hireflix: t('hireflix'),
    h3core: t('h3core'),
    timon: t('timon'),
    mytimeplan: t('mytimeplan'),
    dokobit: t('dokobit'),
    onecrm: t('onecrm'),
    admanager: t('admanager'),
    taktikal: t('taktikal'),
    signet: t('signet'),
    journeys: t('journeys'),
  };
  const headerMap: { [key: string]: JSX.Element } = {
    journeys: <JourneysActions />,
  };
  const headerTabsMap: { [key: string]: JSX.Element } = {
    journeys: <JourneysHeaderTabs />,
  };

  const breadcrumbs = [{ label: t('integrations'), link: '/settings/apps/integrations' }];
  if (routeMap[currentRoute]) {
    breadcrumbs.push({ label: routeMap[currentRoute], link: '' });
  }

  const { hasRolePermission } = usePermissions();

  const canViewIntegrationsSettings = hasRolePermission('company:view-integrations-settings');
  if (!canViewIntegrationsSettings) {
    return null;
  }

  return (
    <Page>
      <Header
        breadcrumbs={<Breadcrumb items={breadcrumbs} />}
        actions={headerMap[currentRoute] ?? <></>}
        tabs={headerTabsMap[currentRoute]}
      />
      {currentRoute !== 'integrations' ? (
        <PageContent>
          <PageContainers.MiddleColumnPage>
            <Routes>
              <Route path="slack" element={<SlackSettingsPage />} />
              <Route path="getaccept" element={<GetAcceptSettingsPage />} />
              <Route path="onboardingform" element={<OnboardingFormSettingsPage />} />
              <Route path="kjarni" element={<KjarniSettingsPage />} />
              <Route path="asana" element={<AsanaSettingsPage />} />
              <Route path="hrmonitor" element={<HRMonitorSettingsPage />} />
              <Route path="relesys" element={<RelesysSettingsPage />} />
              <Route path="hireflix" element={<HireflixSettingsPage />} />
              <Route path="h3core" element={<H3CoreSettingsPage />} />
              <Route path="timon" element={<TimonSettingsPage />} />
              <Route path="mytimeplan" element={<MyTimePlanSettingsPage />} />
              <Route path="dokobit" element={<DokobitSettingsPage />} />
              <Route path="onecrm" element={<OneCRMSettingsPage />} />
              <Route path="admanager" element={<ADManagerSettingsPage />} />
              <Route path="taktikal" element={<TaktikalSettingsPage />} />
              <Route path="signet" element={<SignetSettingsPage />} />
              <Route path="journeys" element={<JourneysSettingsPage />} />
            </Routes>
          </PageContainers.MiddleColumnPage>
        </PageContent>
      ) : (
        <Routes>
          <Route path="integrations" element={<IntegrationItems />} />
        </Routes>
      )}
    </Page>
  );
};
export type ConnectionType = 'notConnected' | 'connected' | 'invalid';

const locale = {
  en: {
    integrations: 'Integrations',
    slack: 'Slack',
    getaccept: 'GetAccept',
    onboardingform: 'Onboarding Form',
    kjarni: 'Kjarni',
    asana: 'Asana',
    hrmonitor: 'HR Monitor',
    relesys: 'Relesys',
    hireflix: 'Hireflix',
    h3core: 'H3 Core',
    timon: 'Timon',
    mytimeplan: 'MyTimePlan',
    dokobit: 'Dokobit',
    onecrm: 'OneCRM',
    admanager: 'AD Manager',
    taktikal: 'Taktikal',
    signet: 'Signet',
    journeys: 'Journeys',
  },
  is: {
    integrations: 'Samþættingar',
    slack: 'Slack',
    getaccept: 'GetAccept',
    onboardingform: 'Innleiðingarform',
    kjarni: 'Kjarni',
    asana: 'Asana',
    hrmonitor: 'HR Monitor',
    relesys: 'Relesys',
    hireflix: 'Hireflix',
    h3core: 'H3 Core',
    timon: 'Timon',
    mytimeplan: 'MyTimePlan',
    dokobit: 'Dokobit',
    onecrm: 'OneCRM',
    admanager: 'AD Manager',
    taktikal: 'Taktikal',
    signet: 'Signet',
    journeys: 'Journeys',
  },
};
